import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { UserMention } from '@/components/UserMention';
import { useGuildRights } from '@/lib/access';
import { ArrowLeft, Send } from 'lucide-react';

interface TicketMessage {
  id: string;
  ticketId: string;
  authorId: string;
  content: string;
  source: 'DISCORD' | 'DASHBOARD';
  createdAt: string;
}

interface Ticket {
  id: string;
  guildId: string;
  userId: string;
  channelId: string;
  status: 'OPEN' | 'CLAIMED' | 'CLOSED';
  subject: string | null;
  claimedBy: string | null;
  createdAt: string;
  closedAt: string | null;
  messages: TicketMessage[];
}

export function TicketDetail() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const qc = useQueryClient();
  const [reply, setReply] = useState('');
  const [silent, setSilent] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => api<{ ticket: Ticket }>(`/api/tickets/${ticketId}`),
    enabled: !!ticketId,
    refetchInterval: 10_000, // pull for new Discord-side messages every 10s
  });
  // Replying belongs to the Tickets module right (lib/access.ts).
  const canReply = useGuildRights(data?.ticket.guildId).can('tickets', 'edit');

  const sendMut = useMutation({
    mutationFn: () =>
      api('/api/actions/ticket-reply', {
        method: 'POST',
        body: JSON.stringify({ ticketId, content: reply, ...(silent ? { silent: true } : {}) }),
      }),
    onSuccess: () => {
      setReply('');
      // Give the bot ~1s to actually post + mirror before refetching.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
        refetch();
      }, 1000);
    },
  });

  if (isLoading) return <p className="text-muted-foreground">Lade Ticket…</p>;
  if (error) return <p className="text-destructive">Fehler: {(error as Error).message}</p>;
  if (!data) return null;

  const { ticket } = data;
  const isClosed = ticket.status === 'CLOSED';

  return (
    <div className="mx-auto max-w-3xl">
      <Link
        to={`/server/${ticket.guildId}/tickets`}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück zur Ticket-Liste
      </Link>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">
            {ticket.subject || 'Ticket'}{' '}
            <span
              className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                ticket.status === 'CLOSED'
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-primary/20 text-primary'
              }`}
            >
              {ticket.status}
            </span>
          </CardTitle>
          <div className="mt-1 text-xs text-muted-foreground">
            User <UserMention id={ticket.userId} /> · Channel <code>#{ticket.channelId}</code> ·{' '}
            {new Date(ticket.createdAt).toLocaleString('de-DE')}
            {ticket.closedAt && (
              <> · geschlossen {new Date(ticket.closedAt).toLocaleString('de-DE')}</>
            )}
          </div>
        </CardHeader>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-base">Chatverlauf ({ticket.messages.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {ticket.messages.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Noch keine Nachrichten mit dem MirrorMessages-Event geloggt. Der Bot startet
              das Logging ab dem nächsten Restart — ältere Nachrichten kannst du direkt in
              Discord im Ticket-Channel sehen.
            </p>
          )}
          <ul className="space-y-3">
            {ticket.messages.map((m) => (
              <li
                key={m.id}
                className={`rounded-md border p-3 text-sm ${
                  m.source === 'DASHBOARD' ? 'border-primary/40 bg-primary/5' : ''
                }`}
              >
                <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                  <UserMention id={m.authorId} />
                  <span>·</span>
                  <span>{new Date(m.createdAt).toLocaleString('de-DE')}</span>
                  {m.source === 'DASHBOARD' && (
                    <span className="rounded-full bg-primary/20 px-2 py-0.5 text-primary">
                      Dashboard
                    </span>
                  )}
                </div>
                <div className="whitespace-pre-wrap break-words">{m.content}</div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {!isClosed && canReply && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Antworten</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea emoji
              mentionGuildId={data?.ticket.guildId}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Deine Antwort..."
              maxLength={2000}
              rows={4}
              disabled={sendMut.isPending}
            />
            <div className="mt-1 flex items-center justify-between gap-3 text-xs text-muted-foreground">
              <Switch
                checked={silent}
                onChange={(e) => setSilent(e.target.checked)}
                label="Leise senden (niemand wird gepingt)"
                className="text-xs"
              />
              <span>{reply.length}/2000</span>
            </div>
            <div className="mt-2 flex justify-end">
              <Button
                disabled={!reply || sendMut.isPending}
                onClick={() => sendMut.mutate()}
                className="gap-2"
              >
                <Send className="h-4 w-4" />
                {sendMut.isPending ? 'Sende…' : 'Antwort senden'}
              </Button>
            </div>
            {sendMut.error && (
              <p className="mt-2 text-sm text-destructive">
                Fehler: {(sendMut.error as Error).message}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
